// Shared state helpers for the editable PrimeVue dropdowns used as search pickers.

/**
 * True when a picker still holds no selection.
 *
 * An editable Dropdown keeps the typed text and the selected object in the same v-model, so the
 * model is a string while the user types and becomes an object once they pick something. "Empty"
 * therefore means null/undefined, or a string with nothing in it. Every focus handler asks this
 * before preloading a default list, so that clicking into a picker the user has already answered
 * does not throw away their selection and refetch.
 */
export function isPickerEmpty(value) {
    return !value || (typeof value === 'string' && value.trim() === '');
}

/**
 * The search term a picker should send to the backend.
 *
 * Only typed text is a search: once the model holds a selected object there is nothing left to
 * look up, so this returns an empty string rather than the selected item's own name. Sending
 * that name back re-ran the search for the drug the user had just chosen, which is a wasted
 * request and, on a slow response, could replace the list under the selection.
 *
 * An empty term is still meaningful on a focus preload, where it means "list everything held".
 * Callers that do not want that must check `fetchDefault` themselves before searching.
 */
export function pickerSearchTerm(value) {
    return typeof value === 'string' ? value.trim() : '';
}

/**
 * What a medication picker's dropdown says when it has nothing to offer.
 *
 * A picker is empty for two different reasons and the two must not read the same. With nothing
 * typed the list is the location's own holding, so an empty one means there is nothing in the
 * safe to pick - which is the case a user could otherwise only tell apart from a broken screen by
 * guessing. With a term typed it means that search found nothing, which says nothing about
 * whether the safe is stocked.
 *
 * `emptyText` overrides the first message for a picker whose source is not the location's imprest
 * stock - a resident's own supply, or a drug catalogue.
 */
export function stockPickerEmptyMessage(pickerValue, emptyText = 'No stock held at this location.') {
    return pickerSearchTerm(pickerValue) ? 'No stock found for that medication.' : emptyText;
}
